import { ASSET_TYPES } from '@porttang/core';
import type { AssetType, Holding, SalePlan, ValidatedHolding } from '@porttang/core';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { firestore } from '../../shared/firebase/client';
import { realizedCollection } from '../realized/realizedRepo';

/** `users/{uid}/holdings` — spec Section 4.3 */
function holdingsCollection(uid: string) {
  return collection(firestore, 'users', uid, 'holdings');
}

function holdingDoc(uid: string, holdingId: string) {
  return doc(firestore, 'users', uid, 'holdings', holdingId);
}

/**
 * แปลงข้อมูลดิบจาก Firestore เป็น Holding
 *
 * **throw เมื่อเจอเอกสารที่ผิดรูป ไม่ข้ามเงียบ ๆ** — เอกสารที่ผิดรูปแปลว่ามีบั๊กหรือ schema
 * เปลี่ยนไปแล้ว ถ้าข้ามทิ้ง ผู้ใช้จะเห็นมูลค่าพอร์ตที่ขาดหายไปโดยไม่รู้ตัว
 * ซึ่งแย่กว่าเห็น error ตรง ๆ (หลักการเดียวกับที่ core ไม่คืน 0 แทนค่าที่ไม่รู้)
 */
function toHolding(id: string, data: DocumentData): Holding {
  const text = (field: string): string => {
    const value: unknown = data[field];
    if (typeof value !== 'string' || value.length === 0) {
      throw new Error(`holding "${id}" มี field "${field}" ที่ไม่ถูกต้อง`);
    }
    return value;
  };

  const assetType: unknown = data['assetType'];
  if (typeof assetType !== 'string' || !ASSET_TYPES.includes(assetType as AssetType)) {
    throw new Error(`holding "${id}" มี assetType ที่ระบบไม่รู้จัก: ${String(assetType)}`);
  }

  const note: unknown = data['note'];

  return {
    id,
    platform: text('platform'),
    assetType: assetType as AssetType,
    symbol: text('symbol'),
    displayName: text('displayName'),
    quantity: text('quantity'),
    avgCost: text('avgCost'),
    currency: text('currency'),
    priceKey: text('priceKey'),
    note: typeof note === 'string' ? note : null,
  };
}

export async function listHoldings(uid: string): Promise<Holding[]> {
  // ⚠️ ห้ามใช้ `orderBy('createdAt')` ใน query
  //
  // Firestore จะ **ตัดเอกสารที่ไม่มี field ที่ใช้เรียงทิ้งไปเงียบ ๆ** ไม่มี error ไม่มีสัญญาณใด ๆ
  // holding ที่ createdAt หายไปด้วยเหตุใดก็ตามจะหายจากพอร์ตโดยที่เจ้าของไม่รู้ตัว
  // ซึ่งคือ "ยอดรวมที่ขาดเงินไปโดยไม่มีใครบอก" แบบเดียวกับที่ระบบนี้ตั้งใจหลีกเลี่ยงมาตลอด
  //
  // จึงดึงมาทั้งหมดแล้วเรียงฝั่ง client แทน — รายการที่ไม่มี createdAt ไปอยู่ท้ายสุด แต่ยังอยู่
  const snapshot = await getDocs(holdingsCollection(uid));

  return snapshot.docs
    .map((snap) => ({
      holding: toHolding(snap.id, snap.data()),
      createdAt: (snap.get('createdAt') as { toMillis?: () => number } | undefined)?.toMillis?.(),
    }))
    .sort(
      (a, b) => (a.createdAt ?? Number.MAX_SAFE_INTEGER) - (b.createdAt ?? Number.MAX_SAFE_INTEGER),
    )
    .map((entry) => entry.holding);
}

/**
 * สร้าง id ฝั่ง client ก่อนเขียน
 *
 * ทำให้ optimistic update ใช้ id จริงได้ตั้งแต่แรก ไม่ต้องมี id ชั่วคราวแล้วสลับทีหลัง
 * ซึ่งเป็นจุดที่ทำให้ UI กระพริบและ key ของ React หลุด
 */
export function newHoldingId(uid: string): string {
  return doc(holdingsCollection(uid)).id;
}

export async function createHolding(
  uid: string,
  holdingId: string,
  holding: ValidatedHolding,
): Promise<void> {
  await setDoc(holdingDoc(uid, holdingId), {
    ...holding,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateHolding(
  uid: string,
  holdingId: string,
  holding: ValidatedHolding,
): Promise<void> {
  await updateDoc(holdingDoc(uid, holdingId), {
    ...holding,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteHolding(uid: string, holdingId: string): Promise<void> {
  await deleteDoc(holdingDoc(uid, holdingId));
}

/**
 * บันทึกการขาย: แก้/ลบ holding + จดรายการขาย **ใน batch เดียว** (spec Section 5.5)
 *
 * แยกกันเขียนเมื่อไหร่ ถ้าตัวที่สองล้ม จะได้พอร์ตที่จำนวนลดแล้วแต่กำไรหายไป
 * หรือกำไรถูกนับแต่ของยังอยู่ครบ — ทั้งสองแบบให้ตัวเลขที่ดูสมเหตุสมผลแต่ผิด
 */
export async function applySale(uid: string, holdingId: string, plan: SalePlan): Promise<void> {
  const batch = writeBatch(firestore);

  if (plan.next === null) {
    batch.delete(holdingDoc(uid, holdingId));
  } else {
    batch.update(holdingDoc(uid, holdingId), {
      quantity: plan.next.quantity,
      avgCost: plan.next.avgCost,
      updatedAt: serverTimestamp(),
    });
  }

  batch.set(doc(realizedCollection(uid)), { ...plan.sale, recordedAt: serverTimestamp() });

  await batch.commit();
}
